/* eslint-disable max-params */
import { computed, inject, InjectionToken, linkedSignal } from '@angular/core';

import {
  GridColumns,
  GridFeatures,
  GridViews,
  WcgMenuOption,
} from '@wcg/frontend-framework';
import {
  BaseGridSignalState,
  constructGridFilters,
  DIALOG_TYPE,
  GridActions,
  handleGridUpdates,
  LoadGridPayload,
} from '@data-access/shared';
import {
  DispatchGridViews,
  GridType,
  GridView,
  GridViewAbstract,
  GridViewDialogData,
  optimizedGridAdvancedFilters,
} from '@data-access/grid-views';

import { WcgTableValueState } from '@shared/interfaces/wcg-table-utils.interface';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import { GridViewsDialogComponent } from '@data-access/grid-views/dialogs/components';
import { toObservable } from '@angular/core/rxjs-interop';
import { skip, takeWhile } from 'rxjs';
import { DEFAULT_TABLE_PARAMS } from '@data-access/configurations/constants/configurations.constant';

/*
 * @NOTE CREATING INJECtioN TOKEN SO COMPONENTS CAN INJECT VIA PROVIDERS ARRAY
 * */
export const BASE_GRID_VIEW_STATE = new InjectionToken<BaseGridSignalState>(
  'BaseGridViewSignalState',
);
export const BASE_LOAD_GRID = new InjectionToken<
  (payload: LoadGridPayload) => void
>('BaseLoadGridFunction');

/**
 * THE FOLLOWING IS THE PARENT CLASS.
 * THAT CAN BE EXTENDED BY LOCAL FACADE SERVICES.
 * THIS CLASS WILL PROVIDE REUSABLE COMPUTED VALUES THAT ARE COMMON ACROSS IRIS-FE.
 * CODE BASE.
 * THIS CLASS WILL ACCEPT THE SIGNAL STORES. THAT EXTENDS BASE_GRID_SIGNAL_STATE.
 *
 */

export class BaseGridViewState extends GridViewAbstract {
  private readonly _wcgTableUtils = inject(WcgTableUtilsService);
  private readonly _signalStore: BaseGridSignalState =
    inject(BASE_GRID_VIEW_STATE);
  private readonly _loadGrid = inject(BASE_LOAD_GRID);

  readonly $columns = computed(() => this._signalStore.$columns());
  readonly $totalCount = computed(() => this._signalStore.$totalCount());
  readonly $isLoading = computed(() => this._signalStore.$isLoading());
  readonly $canDialogClose = computed(() =>
    this._signalStore.$canDialogClose(),
  );
  readonly $error = computed(() => this._signalStore.$error());
  readonly $advancedFilters = computed(() =>
    this._signalStore.$advancedFilters(),
  );
  readonly $searchString = computed(() => this._signalStore.$searchString());
  readonly $selectedView = computed(() => this._signalStore.$selectedView());
  readonly $isTableColumnChanged = computed(() =>
    this._signalStore.$isTableColumnChanged(),
  );
  override readonly $hasFilterChanges = computed(() =>
    this._signalStore.$hasFilterChanges(),
  );
  readonly $gridTotal = computed(() => this._signalStore.$gridTotal());

  syncBaselineAdFilters(): void {
    this._signalStore.syncBaselineAdFilters();
  }
  readonly $gridType = computed(() => {
    const gridType = this._signalStore.$config().features.gridType;
    if (gridType) {
      return gridType as GridType;
    }
    return this._signalStore.$gridType() || 'default';
  });

  readonly $paginationOptions = computed(() => [
    ...this._signalStore.$paginationOptions(),
  ]);
  readonly $pageView = computed(() => this._signalStore.$pageView());
  readonly $wcgTableValues = computed(() => ({
    filters: constructGridFilters(this._signalStore.$advancedFilters()),
    page: this._signalStore.$pageView().page || 1,
    pageSize: this._signalStore.$pageView().pageSize || 25,
    searchValue: this._signalStore.$searchString(),
    sortParam: {
      dir: this._signalStore.$sort()?.sortByOrder || 'asc',
      field: this._signalStore.$sort()?.sortByField || '',
    },
  }));

  readonly $gridFeatures = computed<GridFeatures>(() => {
    const features = this._signalStore.$config().features ?? {};
    return {
      canDownloadAll: !!features.canDownloadAll,
      canExportAll: !!features.canExportAll,
      deleteAll: !!features.deleteAll,
      gridType: features.gridType || '',
      paginationOptions: features.paginationOptions || [],
    };
  });

  readonly $gridViews = linkedSignal({
    computation: (gridViews) => gridViews,
    source: this._signalStore.$gridViews,
  });

  readonly $currentGridView = linkedSignal({
    computation: (currentView) => {
      if (!currentView) {
        return {} as GridViews;
      }
      const gridViews = this.$gridViews();
      if (
        currentView.id?.toLowerCase() !== 'default' &&
        gridViews?.length > 0
      ) {
        return gridViews.reduce((acc, v) => {
          if (v.id?.toLowerCase() === currentView.id?.toLowerCase()) {
            return v;
          }
          return acc;
        }, {} as GridViews);
      }
      return {} as GridViews;
    },
    source: this._signalStore.$selectedView,
  });

  constructor() {
    super();
    this._customListeners();
  }

  resetState(): void {
    this._signalStore.resetState();
  }

  /*
   * GRID OPERATIONS
   * */
  updateGrid(
    actions: GridActions,
    params: WcgTableValueState,
    isTableChanged: boolean,
    updateColumns?: boolean,
  ) {
    if (!params) {
      console.error('Invalid parameters provided to updateOrgGrid');
      return;
    }
    if (['search', 'filter', 'clear'].includes(actions)) {
      this._signalStore.updatePageView({
        page: 1,
        pageSize: this._signalStore.$pageView().pageSize ?? 25,
      });
    }
    handleGridUpdates({
      actions: actions,
      advancedFilters: this._signalStore.$advancedFilters(),
      callBacks: {
        loadGrid: this._loadGrid,
        updateAdvancedFilters: this._signalStore.updateAdvancedFilters,
        updateColumns: this._signalStore.updateColumns,
        updatePageView: this._signalStore.updatePageView,
        updateSearchString: this._signalStore.updateSearchString,
        updateSort: this._signalStore.updateSort,
        updateTableChange: this._signalStore.updateTableChange,
      },
      isTableChanged: isTableChanged,
      pageView: this._signalStore.$pageView(),
      params: params,
      searchString: this._signalStore.$searchString(),
      sort: this._signalStore.$sort(),
      updateColumns: !!updateColumns,
      wcgTableUtils: this._wcgTableUtils,
    });
  }

  /**
   * LOCAL FACADE SERVICE CAN OVERRIDE THE FOLLOWING METHODS.
   **/

  updateGridColumns(columns: GridColumns): void {
    this._signalStore.updateColumns(columns);
    this._signalStore.updateTableChange(true);
  }

  /**
   * @TODO NEED TO DEFINE THE DEFINITION
   * FOR THE FOLLOWING GRID METHODS
   */
  override openGridDialog(viewConfig: GridViewDialogData<GridViews[]>) {
    this._signalStore.resetCanCloseDialog();
    this._signalStore.openDialog({
      component: () => GridViewsDialogComponent,
      componentData: {
        ...viewConfig,
        $canCloseDialog: this.$canDialogClose,
        $error: this.$error,
      },
      config: viewConfig.config,
      type: DIALOG_TYPE.Grid,
    });
  }

  override saveGridView(viewConfig: GridView): void {
    this._signalStore.saveGridView(this._constructViewConfig(viewConfig));
  }
  override updateGridView(viewConfig: GridView): void {
    this._signalStore.updateGridView(this._constructViewConfig(viewConfig));
  }
  override setSelectedView(view: WcgMenuOption, resetSelectView = false): void {
    this._signalStore.setSelectedView(resetSelectView, view);
  }
  override bulkUpdateGridViews(viewConfig: DispatchGridViews): void {
    this._signalStore.bulkUpdateGridViews({ ...viewConfig });
    if (
      viewConfig.grid_views.length !== this._signalStore.$gridViews().length
    ) {
      this.revertGridSettings();
    }
  }

  override revertGridSettings() {
    const _params =
      this._wcgTableUtils.generateTableQueryParams(DEFAULT_TABLE_PARAMS);
    this._signalStore.resetGridSettings({
      params: _params,
      updateColumns: true,
    });
    this._signalStore.updateTableChange(false);
  }

  private _constructViewConfig(viewConfig: GridView) {
    return {
      ...viewConfig,
      advanced_filters: optimizedGridAdvancedFilters(
        viewConfig.advanced_filters!,
      ),
    };
  }
  private _customListeners() {
    toObservable(this.$selectedView)
      .pipe(
        skip(1),
        takeWhile(() => !this._signalStore.$currentStateDestroyed()),
      )
      .subscribe({
        next: (view) => {
          if (view.id?.toLowerCase() !== 'default') {
            this.updateGrid(
              'pagination',
              {
                page: 1,
                pageSize: this._signalStore.$pageView().pageSize ?? 25,
              },
              false,
              false,
            );
          }
        },
      });
  }
}
